import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import SectionHeading from "@/components/common/SectionHeading";
import Breadcrumb from "@/components/common/Breadcrumb";

const values = [
  { icon: "ri-eye-line", title: "真实试读", desc: "每部推荐都经过编辑部亲自试读，不道听途说，不随大流灌水。" },
  { icon: "ri-honour-line", title: "口碑优先", desc: "综合国内外主要平台的长期口碑与读者反馈，用评分与实感说话。" },
  { icon: "ri-heart-line", title: "阅读体验", desc: "从故事、人物与沉浸感出发，讲清楚「为什么值得看」。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "补番清单与榜单每周更新，让收藏夹永远有下一本可看。" },
];

const steps = [
  { n: "01", title: "编辑部选题", desc: "每周从热门、口碑与讨论度中圈定待测作品。" },
  { n: "02", title: "逐本试读", desc: "编辑部完整试读，记录剧情、人物与阅读体验。" },
  { n: "03", title: "口碑核查", desc: "对照多平台评分与读者长评，剔除水分。" },
  { n: "04", title: "整理入榜", desc: "按栏目归类，生成补番清单与推荐榜单发布。" },
];

const testimonials = [
  {
    name: "阿鹿",
    role: "追番三年的读者",
    text: "跟着补番清单把冷门宝藏挖了个遍，蓝色时期、杀手寓言都是在这里发现的，太对口味了！",
  },
  {
    name: "木木",
    role: "刚入坑的萌新",
    text: "一开始不知道看什么，照着高分榜单闭眼入，一部都没踩雷，漫荒被彻底治好了。",
  },
  {
    name: "老K",
    role: "二十年老读者",
    text: "编辑的推荐理由写得真诚又到位，不吹不黑，看得出是真读过才敢下结论。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 我爱漫画网 | 漫荒推荐 · 补番清单内容平台",
    description:
      "我爱漫画网是一站式漫画推荐与补番清单内容平台，坚持真实试读与口碑优先，收录高分漫画、冷门佳作与必看榜单，帮助读者告别剧荒。",
    path: "/about",
  });

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我爱漫画网",
    description:
      "我爱漫画网是一站式漫画推荐与补番清单内容平台，收录高分漫画、冷门佳作与必看榜单。",
    mainEntity: {
      "@type": "Organization",
      name: "我爱漫画网",
      slogan: "告别剧荒，找到下一本好漫画",
      description: "面向漫荒读者的漫画推荐与策展内容站",
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />

      <section className="relative overflow-hidden bg-foreground-950 text-background-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-foreground-950 to-foreground-900" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.1),transparent_45%)]" />
        <div className="container-page relative z-10 flex flex-col items-start py-16 md:py-20">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-background-50/10 px-4 py-1.5 text-sm text-background-100 backdrop-blur-sm">
            <i className="ri-information-line text-accent-400" />
            关于我们
          </span>
          <h1 className="font-heading text-3xl font-bold md:text-4xl">让每一个漫荒时刻，都有好漫画可看</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            我爱漫画网诞生于一个朴素的念头：当你想看漫画却不知道看什么时，应该有一个值得信赖的地方告诉你"接下来看这本"。
          </p>
        </div>
      </section>

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      {/* 品牌介绍 + 网站定位 */}
      <section className="container-page section-pad">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 md:gap-8">
          <div>
            <SectionHeading kicker="品牌介绍" title="我们是谁" />
            <p className="leading-relaxed text-foreground-700">
              我们是一群把"读完一本好漫画"当成日常的编辑，因为自己也曾无数次在书架前徘徊、在搜索页里刷到深夜，才更懂漫荒的滋味。
            </p>
            <p className="mt-4 leading-relaxed text-foreground-700">
              于是我们创建了<span className="font-bold text-primary-700">我爱漫画网</span>，把真正经得起读的高分漫画、冷门佳作与必看榜单整理成册，
              配上真诚的推荐理由和实用的补番清单，让每一位读者都能快速定位到适合自己的下一本作品。
            </p>
          </div>
          <div>
            <SectionHeading kicker="网站定位" title="我们要做什么" />
            <p className="leading-relaxed text-foreground-700">
              我爱漫画网的定位是"漫荒推荐与补番清单内容站"，不做搬运、不做剧透轰炸，
              只做一件事：以编辑的审美与读者的视角，帮你从海量作品中筛出真正值得花时间的那一本。
            </p>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />按栏目快速定位：必看、高分、冷门、补番。</li>
              <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />按关键词搜索，找到想看的类型与作品。</li>
              <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />每周更新的清单，让阅读计划不落空。</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="bg-background-100">
        <div className="container-page section-pad">
          <SectionHeading kicker="内容原则" title="我们坚持的内容标准" align="center" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200/80 bg-background-50 p-6 text-center">
                <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-700">
                  <i className={v.icon} />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 发展理念 */}
      <section className="container-page section-pad">
        <SectionHeading kicker="发展理念" title="一份推荐是怎么诞生的" description="从选题到发布，我们用一套可复用的流程保证每一期推荐都靠谱。" />
        <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {steps.map((s) => (
            <li key={s.n} className="relative rounded-lg border border-background-200/80 bg-background-100 p-6">
              <span className="font-heading text-3xl font-bold text-primary-200">{s.n}</span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 用户口碑 */}
      <section className="bg-background-100">
        <div className="container-page section-pad">
          <SectionHeading kicker="用户口碑" title="读者怎么说" align="center" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200/80 bg-background-50 p-6">
                <div className="mb-3 flex items-center gap-1 text-accent-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page pb-12 md:pb-16">
        <div className="flex flex-col items-center gap-4 rounded-lg bg-secondary-100 px-6 py-10 text-center md:px-10 md:py-12">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-2xl text-background-50">
            <i className="ri-message-3-line" />
          </span>
          <h2 className="font-heading text-2xl font-bold text-foreground-950">想投稿或合作？欢迎联系我们</h2>
          <p className="max-w-xl text-sm leading-relaxed text-foreground-700">
            如果你有想安利的好漫画，或想与我们一起做更多有意思的补番内容，欢迎随时联系。
          </p>
          <Link to="/contact" className="mt-1 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            <i className="ri-mail-send-line" />
            联系我们
          </Link>
        </div>
      </section>
    </>
  );
}